import {
  Activity,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { ChevronLeftIcon, ChevronRightIcon, FileIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { LabelStackView } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { H1, H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Loader } from "simplydo/react/ui/loader";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import { Placeholder, PlaceholderLine } from "simplydo/react/ui/placeholder";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import IdeaBody from "components/ideas/Body";
import { LaneInfo } from "components/ideas/Header";
import { IdeaProvider } from "components/ideas/IdeaProvider";
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import { useIdea } from "components/ideas/hooks/useIdea";
import {
  AnonymousChip,
  IdeaBusinessProfileChip,
  UserChip,
} from "components/lib/Chips";
import { TagView } from "components/lib/Choosers/Tags/TagModalChooser";
import { HoverAction, HoverActions } from "components/lib/HoverActions";
import SlidingModal from "components/lib/SlidingModal";

import PreviewSideWindow from "./PreviewSideWindow/PreviewSideWindow";
import type {
  HandledIdeaPreviewProps,
  IdeaPreviewChallenge,
  IdeaPreviewIdea,
  IdeaPreviewProps,
} from "./types";

export type { HandledIdeaPreviewProps, IdeaPreviewProps } from "./types";

const noopUpdateProjectLane = (
  _ideaIds: string[],
  _laneId: string,
  _order?: number,
) => {};

const ModalContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const PreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: #f2f2f3;
`;

const PreviewHeader = styled.div<{ $coverImage?: string }>`
  width: 100%;
  position: relative;

  .overlay {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.21);
    z-index: 11;
  }
  .overlay-body {
    position: relative;
    z-index: 12;
    display: flex;
    flex-direction: column;
    padding: 10px;
  }
  top: 0;
  left: 0;
  background-image: ${({ $coverImage }) =>
    $coverImage ? `url(${$coverImage})` : "none"};
  background-size: ${({ $coverImage }) => ($coverImage ? "cover" : "auto")};
  background-position: ${({ $coverImage }) =>
    $coverImage ? "center center" : "initial"};
  background-color: ${({ $coverImage }) =>
    !$coverImage ? "#e9ebee" : "transparent"};
  border-bottom: 1px solid lightgray;
  z-index: 999;
  min-height: 175px;
  overflow: hidden;
  text-overflow: ellipsis;
  .ui.button {
    border: 0.5px solid gray;
  }
  h1 {
    font-size: 2.3em;
    margin-top: 20px;
    margin-bottom: 10px;
    color: white;
    line-height: 1.15em;
  }
`;

const IdeaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 13;
  background-color: #f2f2f3;
`;

const BodyContainer = styled.div`
  width: 100%;
  padding: 10px;
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  &.wraps {
    flex-wrap: wrap;
    margin-top: -7.5px;
    > * {
      margin-top: 7.5px;
    }
  }
`;

type IdeaPreviewComponentProps = IdeaPreviewProps;

const IdeaPreview = ({
  isOpen,

  challenge: propChallenge,
  onCloseModal = () => {},
  onAfterCloseModal,

  moveIdea,
  canGoNextIdea,
  canGoPrevIdea,
  assessmentEnabled,
  updateProjectLane = noopUpdateProjectLane,
  removeIdeaFromMerge,
  removingIdeaFromMerge,
  currentIdeaPosition,
  totalIdeas,
  redirectToBoard,
  page = 1,
  limit = 0,
}: IdeaPreviewComponentProps) => {
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const [idea, ideaQuery] = useIdea();
  const loading = ideaQuery.isLoading;

  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [modalIdeaWidth, setModalIdeaWidth] = useState(0);
  const [commentRef, setCommentRef] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setSectionRef = (ref: HTMLDivElement | null, itemId: string) => {
    sectionRefs.current[itemId] = ref;

    if (itemId === "comments") {
      setCommentRef(ref);
    }
  };
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setFieldRef = (ref: HTMLDivElement | null, itemId: string) => {
    fieldRefs.current[itemId] = ref;
  };

  const { t } = useTranslation();

  const theme = useTheme();
  const challengeId = idea?.challenge;
  const board = useMemo(
    () => idea?.projectManagement?.boards?.find((b) => b.forId === challengeId),
    [idea, challengeId],
  );
  const ideaId = idea?._id;

  const { clearAssessmentRound } = useAssessmentEditor(idea);

  // only show pm board management actions if the user can manage challenge (collaborator/owner/admins) or is assigned to the idea
  const canManageChallenge = util.canManageChallenge(
    user!,
    propChallenge ?? idea?.ownerChallenge,
  );
  const canViewProjectBoard =
    canManageChallenge ||
    ((board?.assignees || []).indexOf(user?._id ?? "") > -1 &&
      idea?.ownerChallenge?.projectManagementVisibility === "users") ||
    util.hasPermission(user!, "challenge.viewProjectBoard", challengeId ?? "");

  const handleCloseModal = useCallback(() => {
    setModalIdeaWidth(0);
    clearAssessmentRound();
    onCloseModal();
  }, [clearAssessmentRound, onCloseModal]);

  const onOpenPreview = useCallback(() => {
    const rect = previewContainerRef.current?.getBoundingClientRect();
    setModalIdeaWidth(rect?.width ?? 0);
  }, []);

  useEffect(() => {
    if (idea) {
      api.setQueryData(api.challenges.getIdeas, [idea.challenge], (prev) => ({
        ...prev,
        ideas: (prev.ideas || []).map((oldIdea) => {
          if (oldIdea._id !== idea._id) return oldIdea;
          return { ...oldIdea, currentUserHasViewed: true };
        }),
      }));
    }
  }, [idea]);

  useEffect(() => {
    if (commentRef && location.hash === "#comments") {
      commentRef.scrollIntoView({ behavior: "smooth", block: "start" });
      navigate(location.search);
    }
  }, [commentRef, location.hash, location.search, navigate]);

  return (
    <SlidingModal
      isOpen={isOpen}
      onOpen={onOpenPreview}
      onClose={handleCloseModal}
      onAfterClose={onAfterCloseModal}
      width={theme?.sizes.isComputer ? "60%" : "100%"}
      ref={previewContainerRef}
    >
      <ModalContainer>
        {theme.sizes.isComputer && modalIdeaWidth > 0 ? (
          <HoverActions $width={modalIdeaWidth}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <HoverAction onClick={() => handleCloseModal()}>
                Close
              </HoverAction>
              {moveIdea && isOpen && (canGoPrevIdea || canGoNextIdea) ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <Popup
                    on="hover"
                    trigger={
                      <div>
                        <HoverAction
                          onClick={() => moveIdea("prev")}
                          margined={!(currentIdeaPosition && totalIdeas)}
                          disabled={!canGoPrevIdea}
                        >
                          <Icon icon={ChevronLeftIcon} />
                        </HoverAction>
                      </div>
                    }
                  >{`Go to previous ${t("generic.idea")}`}</Popup>

                  {currentIdeaPosition &&
                    totalIdeas &&
                    (canGoNextIdea || canGoPrevIdea) && (
                      <HoverAction disabled>
                        {t("common:capitalise", { key: "generic.idea" })}{" "}
                        {(page - 1) * limit + currentIdeaPosition} of{" "}
                        {totalIdeas}
                      </HoverAction>
                    )}
                  <Popup
                    trigger={
                      <div>
                        <HoverAction
                          onClick={() => moveIdea("next")}
                          disabled={!canGoNextIdea}
                        >
                          <Icon icon={ChevronRightIcon} />
                        </HoverAction>
                      </div>
                    }
                  >{`Go to next ${t("generic.idea")}`}</Popup>
                </div>
              ) : null}
            </div>

            <div className="hover-actions-inner">
              <PreviewSideWindow
                assessmentEnabled={assessmentEnabled}
                updateProjectLane={updateProjectLane}
                challenge={propChallenge as IdeaPreviewChallenge | undefined}
                sectionRefs={sectionRefs}
                fieldRefs={fieldRefs}
                scrollRef={previewContainerRef}
              />
            </div>
          </HoverActions>
        ) : null}
        <PreviewContainer>
          {!loading && !idea ? (
            <Container style={{ height: "100vh" }}>
              <Message color="red" style={{ margin: "2rem 0" }}>
                <MessageHeader>
                  Unable to load {t("generic.idea")}
                </MessageHeader>
                <p>
                  Sorry, we were unable to load this {t("generic.idea")}. Please
                  try again later.
                </p>
              </Message>
              <Button onClick={() => handleCloseModal()}>
                <Icon icon={ChevronLeftIcon} />
                {`Close`}
              </Button>
            </Container>
          ) : (
            <>
              <PreviewHeader
                $coverImage={util.ideaCoverImage(idea, "2560x400")}
              >
                <div className="overlay" />
                <div className="overlay-body">
                  <FlexRow>
                    <ModalContainer style={{ justifyContent: "flex-end" }}>
                      {!loading ? (
                        <>
                          <ButtonLink
                            size="small"
                            color="primary"
                            to={`/ideas/${ideaId}`}
                          >
                            <Icon icon={FileIcon} />
                            {`Open full ${t("generic.idea")}`}
                          </ButtonLink>
                          {redirectToBoard && (
                            <ButtonLink
                              size="small"
                              color="primary"
                              to={`/challenges/${challengeId}/board?idea=${ideaId}`}
                            >
                              <Icon icon={FileIcon} />
                              {`View in project board`}
                            </ButtonLink>
                          )}
                          {!theme.sizes.isComputer ? (
                            <Button
                              size="small"
                              onClick={() => handleCloseModal()}
                            >{`Close preview`}</Button>
                          ) : null}
                        </>
                      ) : null}
                    </ModalContainer>
                  </FlexRow>
                  <div style={{ paddingLeft: 10 }}>
                    <div style={{ marginTop: 10 }}>
                      {loading ? (
                        <Placeholder style={{ width: 400, marginTop: 60 }}>
                          <PlaceholderLine style={{ height: 40, width: 400 }} />
                          <PlaceholderLine style={{ height: 25, width: 200 }} />
                          <PlaceholderLine style={{ height: 30, width: 200 }} />
                        </Placeholder>
                      ) : (
                        <>
                          <H1>{idea?.name}</H1>
                          <H5 style={{ marginTop: 0, color: "white" }}>
                            <span style={{ textTransform: "uppercase" }}>
                              {t("generic.idea")}
                            </span>
                            {idea.ownerChallenge && (
                              <span style={{ color: "white" }}>
                                {" "}
                                For{" "}
                                <Link
                                  style={{ color: "white" }}
                                  to={`/challenges/${idea.challenge}`}
                                >
                                  {idea.ownerChallenge?.name}
                                </Link>
                              </span>
                            )}
                          </H5>
                          <div style={{ display: "flex", marginBottom: 10 }}>
                            {((board as { laneObject?: { laneCount?: number } })
                              ?.laneObject?.laneCount ?? 0) > 0 &&
                              util.canManageChallenge(
                                user!,
                                idea.ownerChallenge,
                              ) && (
                                <>
                                  <LaneInfo
                                    board={board}
                                    idea={idea}
                                    style={{ marginRight: 10 }}
                                  />
                                  <div
                                    style={{
                                      borderLeft: "2px solid white",
                                      height: 24,
                                      marginRight: 10,
                                    }}
                                  ></div>
                                </>
                              )}

                            {!loading ? (
                              idea.isSubmitted ? (
                                <Popup
                                  trigger={
                                    <Label
                                      style={{ maxHeight: "fit-content" }}
                                      color="green"
                                    >
                                      Submitted
                                    </Label>
                                  }
                                >{`Submitted ${formatDate(idea.submittedAt)}`}</Popup>
                              ) : (
                                <Label
                                  style={{ maxHeight: "fit-content" }}
                                  color="yellow"
                                >
                                  Draft
                                </Label>
                              )
                            ) : null}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <div style={{ color: "#fff" }}>
                              <TagView tags={idea.ownerTags ?? []} />
                              <LabelStackView
                                labels={idea.labels ?? []}
                                horizontal
                              />
                              <span style={{ color: "white", marginRight: 5 }}>
                                Created by{" "}
                              </span>
                              {!idea.authors || idea.authors.length === 0 ? (
                                <AnonymousChip />
                              ) : null}
                              {idea.lockedOwnerIdeaBusinessProfile ? (
                                <IdeaBusinessProfileChip
                                  ideaBusinessProfile={
                                    idea.lockedOwnerIdeaBusinessProfile
                                  }
                                  inverted
                                  style={{ marginRight: 15 }}
                                />
                              ) : null}
                              {idea.authors
                                ? idea.authors.map((a) => (
                                    <UserChip
                                      user={a}
                                      key={a._id}
                                      inverted
                                      style={{
                                        marginRight: 15,
                                        marginBottom: 4,
                                        marginTop: 10,
                                      }}
                                    />
                                  ))
                                : null}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </PreviewHeader>
              <IdeaContainer>
                <BodyContainer>
                  {!theme.sizes.isComputer &&
                  idea &&
                  !loading &&
                  canViewProjectBoard ? (
                    <PreviewSideWindow
                      assessmentEnabled={assessmentEnabled}
                      updateProjectLane={updateProjectLane}
                      challenge={
                        propChallenge as IdeaPreviewChallenge | undefined
                      }
                      fieldRefs={fieldRefs}
                      sectionRefs={sectionRefs}
                      scrollRef={previewContainerRef}
                    />
                  ) : null}
                  <IdeaBody
                    key={idea?._id ?? ""}
                    idea={idea}
                    setSectionRef={setSectionRef}
                    sectionRefs={sectionRefs}
                    setFieldRef={setFieldRef}
                    fieldRefs={fieldRefs}
                    loading={loading}
                    isEditing={false}
                    scrollRef={previewContainerRef}
                    removeIdeaFromMerge={removeIdeaFromMerge}
                    removingIdeaFromMerge={removingIdeaFromMerge}
                    updateIdea={(value, path) => {
                      util.onUpdateIdea(idea, value, path);
                    }}
                  />
                </BodyContainer>
              </IdeaContainer>
            </>
          )}
        </PreviewContainer>
      </ModalContainer>
    </SlidingModal>
  );
};

type HandledIdeaPreviewComponentProps = HandledIdeaPreviewProps;

export const HandledIdeaPreview = ({
  ideaId: propIdeaId,
  removeIdeaFromMerge: propRemoveIdeaFromMerge,
  updateProjectLane: propUpdateProjectLane,
  ...rest
}: HandledIdeaPreviewComponentProps) => {
  const [ideaId, setIdeaId] = useState<string | null>(propIdeaId);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!propIdeaId) {
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
    setIdeaId(propIdeaId);
  }, [propIdeaId]);

  const ideaQuery = useQuery(
    initUseQuery(api.ideas.get, [ideaId!], {
      isEnabled: !!ideaId,
      select: (data) => ({ _id: data._id, challenge: data.challenge }),
    }),
  );

  const removeIdeaFromMerge = useCallback(
    (mergeIdeaId: string, childIdeaId: string) => {
      propRemoveIdeaFromMerge?.(mergeIdeaId, childIdeaId, () => {
        if (mergeIdeaId === ideaId && ideaId) {
          api.setQueryData(api.ideas.get, [ideaId], (prev) => {
            if (!prev || typeof prev !== "object") return prev;
            const ideaData = prev as IdeaPreviewIdea;
            return {
              ...(ideaData as object),
              children: (ideaData.children ?? []).filter(
                (c: string) => c !== childIdeaId,
              ),
              ownerChildren: (ideaData.ownerChildren ?? []).filter(
                (c) => c._id !== childIdeaId,
              ),
            } as IdeaPreviewIdea;
          });
        }
      });
    },
    [ideaId, propRemoveIdeaFromMerge],
  );

  const updateProjectLane = useCallback(
    (movingIdeaIds: string[], laneId: string, order: number = 0) => {
      const challengeId = ideaQuery.data?.challenge;
      if (!challengeId || !movingIdeaIds.length || !laneId) {
        return;
      }

      api.boards.moveProjectIdeas(
        challengeId,
        {
          ideaIds: movingIdeaIds,
          targetLane: laneId,
          order,
          sendAnnouncements: [],
        },
        ({ updatedIdeas }) => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => {
            if (!prev?.ideas || !updatedIdeas) return prev;

            return {
              ...prev,
              ideas: prev.ideas.map((idea) => {
                const matchingUpdatedIdea = updatedIdeas.find(
                  (updated) => updated._id === idea._id,
                );
                if (!matchingUpdatedIdea) return idea;
                return {
                  ...idea,
                  projectManagement: matchingUpdatedIdea.projectManagement,
                };
              }),
            };
          });
        },
        () => {},
      );
    },
    [ideaQuery.data?.challenge],
  );

  return (
    <IdeaProvider ideaId={ideaId ?? undefined}>
      <IdeaPreview
        {...rest}
        isOpen={isOpen}
        removeIdeaFromMerge={removeIdeaFromMerge}
        updateProjectLane={propUpdateProjectLane ?? updateProjectLane}
        onAfterCloseModal={() => setIdeaId(null)}
      />
    </IdeaProvider>
  );
};

export default IdeaPreview;
